/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Custom Buttons for app
 */
import { JSX, PureComponent } from 'react';
import { Button as AntButton, ButtonProps } from 'antd';

import AppButton from './AppButton';

import EditEligibilityRequest from './EditEligibilityRequest';
import ViewEligibilityResponse from './ViewEligibilityResponse';
import ResubmitEligibilityRequest from './ResubmitEligibilityRequest';
import PrintEligibilityResponse from './PrintEligibilityResponse';

import UpdateProviderStatus from './UpdateProviderStatus';
import EditProvider from './EditProvider';
import ProviderActions from './ProviderActions';
import AddProvider from './AddProvider';
import Add from './Add';
import Remove from './Remove';

import AddLocation from './AddLocation';
import EditLocation from './EditLocation';
import UpdateLocationStatus from './UpdateLocationStatus';
import AddKeycloakUser from './AddKeycloakUser';
import EditKeycloakUser from './EditKeycloakUser';
import IdentityVerification from './IdentityVerification';

import CancelBatchEligibilityRequest from './CancelBatchEligibilityRequest';
import EditBatchUpload from './EditBatchUpload';
import ViewBatchResponse from './ViewBatchResponse';
import CancelBatchReason from './CancelBatchReason';
import ViewBatchMapping from './ViewBatchMapping';
import NewEligibilityRequest from './NewEligibilityRequest';
import NewBatchUpload from './NewBatchUpload';
import OpenStripePortal from './OpenStripePortal';
import ViewPlanFeatures from './ViewPlanFeatures';
import DownloadInvoice from './DownloadInvoice';
import CancelActivePlan from './CancelActivePlan';
import RetryPayment from './RetryPayment';
import ExportEligibilityBatchTemplate from './ExportEligibilityBatchTemplate';
import EligibilityBatchTemplateFields from './EligibilityBatchTemplateFields';
import EditClaimRequest from './EditClaimRequest';
import ViewClaimResponse from './ViewClaimResponse';
import ResubmitClaimRequest from './ResubmitClaimRequest';
import PrintClaimResponse from './PrintClaimResponse';
import ViewEligibilityRequestChecks from './ViewEligibilityRequestChecks';
import TogglePayerStatus from './TogglePayerStatus';
import EditPayerDetails from './EditPayerDetails';
import EnrollmentDetails from './EnrollmentDetails';
import AddPayer from './AddPayer';
import AddPayerModal from './AddPayerModal';
import ChangeEnrollmentStatus from './ChangeEnrollmentStatus';
import RequestEnrollment from './RequestEnrollment';
import NewClaimStatusRequest from './NewClaimStatusRequest';
import ViewClaimStatusChecks from './ViewClaimStatusChecks';
import ExportEligibilityData from './ExportEligibilityData';
import BatchResubmitEligibilityRequests from './BatchResubmitEligibilityRequests';
import ExportClaimStatusData from './ExportClaimStatusData';
import AddToManualWorkList from './AddToManualWorkList';
import NewClaimWorkList from './NewClaimWorkList';
import ClaimWorkListActions from './ClaimWorkListActions';
import ShowWorklists from './ShowWorklists';
import ViewClaimWorklistDetails from './ViewClaimWorklistDetails';
import AddFavoriteCode from './AddFavoriteCode';
import EditCodeNotes from './EditCodeNotes';
import ClaimActivityLogActions from './ClaimActivityLogActions';
import AddNote from './AddNote';
import ClaimActions from './ClaimActions';
import PostPaymentButton from './PostPaymentButton';
import ShowCodeAttributeDetails from './ShowCodeAttributeDetails';
import ShowICD10Attribute from './ShowICD10Attribute';
import RemoveClaimFromManualWorklist from './RemoveClaimFromManualWorklist';
import ChangeClaimWorkState from './ChangeClaimWorkState';
import ClaimTableActions from './ClaimTableActions';
import EntityNote from './EntityNote';
import AddInsurance from './AddInsurance';
import MoveToPreviousInsurance from './MoveToPreviousInsurance';
import ShowClaimCode from './ShowClaimCode';
import ResubmitClaim from './ResubmitClaim';
import EditGuarantor from './EditGuarantor';
import InsuranceActions from './InsuranceActions';
import RecordPayment from './RecordPayment';
import PaymentsTableActions from './PaymentsTableActions';
import EditPaymentDetails from './EditPaymentDetails';
import AllocatePayment from './AllocatePayment';
import EditPaymentAllocation from './EditPaymentAllocation';
import RefundAllocation from './RefundAllocation';
import DownloadBlob from './DownloadBlob';
import DeletePaymentRecord from './DeletePaymentRecord';
import ReconcilePayment from './ReconcilePayment';
import ERAActions from './ERAActions';
import RevertReconcilePayment from './RevertReconcilePayment';
import AllocatedOverview from './AllocatedOverview';
import GuarantorRefund from './GuarantorRefund';
import GuarantorUnallocatedAmountActions from './GuarantorUnallocatedAmountActions';
import PaymentAllocationUnallocatedAmountActions from './PaymentAllocationUnallocatedAmountActions';
import SortTableData from './SortTableData';
import AddClaimAction from './AddClaimAction';
import DeleteClaimAction from './DeleteClaimAction';
import DeleteAttachment from './DeleteAttachment';
import UploadAttachment from './UploadAttachment';
import BatchEraPosting from './BatchEraPosting';
import FileSecondaryClaim from './FileSecondaryClaim';
import AddWriteOffReason from './AddWriteOffReason';
import DeleteWriteOffReason from './DeleteWriteOffReason';
import SearchClaimCodes from './SearchClaimCodes';
import DeletePaymentRefund from './DeletePaymentRefund';
import DeleteGuarantorRefund from './DeleteGuarantorRefund';
import GuarantorRefundActions from './GuarantorRefundActions';
import ExportStatement from './ExportStatement';
import FilePrimaryClaim from './FilePrimaryClaim';
import BatchPaymentPostHistory from './BatchPaymentPostHistory';
import WriteOff from './WriteOff';
import DraftClaimActions from './DraftClaimActions';
import VoidClaim from './VoidClaim';
import PatientActions from './PatientAction';
import GuarantorActions from './GuarantorActions';

/**
 * Custom Action Buttons for app
 */
class Button extends PureComponent<ButtonProps> {
	static readonly UpdateProviderStatus = UpdateProviderStatus;
	static readonly EditProvider = EditProvider;
	static readonly ProviderActions = ProviderActions;
	static readonly AddProvider = AddProvider;
	static readonly Add = Add;
	static readonly Remove = Remove;
	static readonly SortTableData = SortTableData;
	static readonly DeleteAttachment = DeleteAttachment;
	static readonly UploadAttachment = UploadAttachment;
	/* Location */
	static readonly AddLocation = AddLocation;
	static readonly EditLocation = EditLocation;
	static readonly UpdateLocationStatus = UpdateLocationStatus;

	/* Keycloak User */
	static readonly AddKeycloakUser = AddKeycloakUser;
	static readonly EditKeycloakUser = EditKeycloakUser;

	/** Render */

	/* Eligibility */
	static readonly EditEligibilityRequest = EditEligibilityRequest;
	static readonly ViewEligibilityResponse = ViewEligibilityResponse;
	static readonly ResubmitEligibilityRequest = ResubmitEligibilityRequest;
	static readonly PrintEligibilityResponse = PrintEligibilityResponse;
	static readonly CancelBatchEligibilityRequest = CancelBatchEligibilityRequest;
	static readonly EditBatchUpload = EditBatchUpload;
	static readonly ViewBatchResponse = ViewBatchResponse;
	static readonly CancelBatchReason = CancelBatchReason;
	static readonly ViewBatchMapping = ViewBatchMapping;
	static readonly NewEligibilityRequest = NewEligibilityRequest;
	static readonly NewBatchUpload = NewBatchUpload;
	static readonly ViewEligibilityRequestChecks = ViewEligibilityRequestChecks;
	static readonly ExportEligibilityData = ExportEligibilityData;
	static readonly BatchResubmitEligibilityRequests = BatchResubmitEligibilityRequests;
	static readonly ExportEligibilityBatchTemplate = ExportEligibilityBatchTemplate;
	static readonly EligibilityBatchTemplateFields = EligibilityBatchTemplateFields;

	/* Claim */
	static readonly EditClaimRequest = EditClaimRequest;
	static readonly ViewClaimResponse = ViewClaimResponse;
	static readonly ResubmitClaimRequest = ResubmitClaimRequest;
	static readonly PrintClaimResponse = PrintClaimResponse;
	static readonly NewClaimStatusRequest = NewClaimStatusRequest;
	static readonly ViewClaimStatusChecks = ViewClaimStatusChecks;
	static readonly ShowWorklists = ShowWorklists;
	static readonly ViewClaimWorklistDetails = ViewClaimWorklistDetails;
	static readonly AddToManualWorkList = AddToManualWorkList;
	static readonly NewClaimWorkList = NewClaimWorkList;
	static readonly ClaimWorkListActions = ClaimWorkListActions;
	static readonly ClaimActivityLogActions = ClaimActivityLogActions;
	static readonly AddNote = AddNote;
	static readonly ClaimActions = ClaimActions;
	static readonly ResubmitClaim = ResubmitClaim;
	static readonly FileSecondaryClaim = FileSecondaryClaim;
	static readonly SearchClaimCodes = SearchClaimCodes;
	static readonly FilePrimaryClaim = FilePrimaryClaim;
	static readonly DraftClaimActions = DraftClaimActions;
	static readonly VoidClaim = VoidClaim;

	static readonly IdentityVerification = IdentityVerification;
	static readonly OpenStripePortal = OpenStripePortal;
	static readonly ViewPlanFeatures = ViewPlanFeatures;
	static readonly DownloadInvoice = DownloadInvoice;
	static readonly CancelActivePlan = CancelActivePlan;
	static readonly RetryPayment = RetryPayment;
	static readonly ExportClaimStatusData = ExportClaimStatusData;

	static readonly EditPayerDetails = EditPayerDetails;
	static readonly TogglePayerStatus = TogglePayerStatus;
	static readonly EnrollmentDetails = EnrollmentDetails;
	static readonly ChangeEnrollmentStatus = ChangeEnrollmentStatus;
	static readonly RequestEnrollment = RequestEnrollment;
	static readonly AddPayer = AddPayer;
	static readonly AddPayerModal = AddPayerModal;

	static readonly PostPaymentButton = PostPaymentButton;
	static readonly RemoveClaimFromManualWorklist = RemoveClaimFromManualWorklist;
	static readonly ChangeClaimWorkState = ChangeClaimWorkState;
	static readonly ClaimTableActions = ClaimTableActions;
	static readonly EntityNote = EntityNote;
	static readonly AddInsurance = AddInsurance;
	static readonly MoveToPreviousInsurance = MoveToPreviousInsurance;
	static readonly InsuranceActions = InsuranceActions;
	static readonly ReconcilePayment = ReconcilePayment;
	static readonly RevertReconcilePayment = RevertReconcilePayment;
	static readonly ERAActions = ERAActions;
	static readonly AddClaimAction = AddClaimAction;
	static readonly DeleteClaimAction = DeleteClaimAction;

	/* Payments */
	static readonly EditGuarantor = EditGuarantor;
	static readonly GuarantorRefund = GuarantorRefund;
	static readonly GuarantorActions = GuarantorActions;
	static readonly GuarantorUnallocatedAmountActions = GuarantorUnallocatedAmountActions;
	static readonly PaymentAllocationUnallocatedAmountActions = PaymentAllocationUnallocatedAmountActions;
	static readonly RecordPayment = RecordPayment;
	static readonly PaymentsTableActions = PaymentsTableActions;
	static readonly EditPaymentDetails = EditPaymentDetails;
	static readonly AllocatePayment = AllocatePayment;
	static readonly EditPaymentAllocation = EditPaymentAllocation;
	static readonly RefundAllocation = RefundAllocation;
	static readonly DeletePaymentRecord = DeletePaymentRecord;
	static readonly DownloadBlob = DownloadBlob;
	static readonly AllocatedOverview = AllocatedOverview;
	static readonly BatchEraPosting = BatchEraPosting;
	static readonly DeletePaymentRefund = DeletePaymentRefund;
	static readonly DeleteGuarantorRefund = DeleteGuarantorRefund;
	static readonly GuarantorRefundActions = GuarantorRefundActions;
	static readonly ExportStatement = ExportStatement;
	static readonly BatchPaymentPostHistory = BatchPaymentPostHistory;
	static readonly WriteOff = WriteOff;
	static readonly PatientActions = PatientActions;
	/**
	 * Code Explorer
	 */
	static readonly AddFavoriteCode = AddFavoriteCode;
	static readonly EditCodeNotes = EditCodeNotes;
	static readonly ShowCodeAttributeDetails = ShowCodeAttributeDetails;
	static readonly ShowICD10Attribute = ShowICD10Attribute;
	static readonly ShowClaimCode = ShowClaimCode;

	/**
	 * Settings
	 */
	static readonly AddWriteOffReason = AddWriteOffReason;
	static readonly DeleteWriteOffReason = DeleteWriteOffReason;

	render(): JSX.Element {
		const { children, ...props } = this.props;
		return <AntButton {...props}>{children}</AntButton>;
	}
}

export { AppButton };
export default Button;
